import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getTodayMeeting } from '../lib/meetings';

import { HOME_LANDING_CONTENT } from '../content/homeLandingContent';
import { apiClient } from '../lib/api/client';
import { getPublicIndustriesSought } from '../lib/public';
import { getMyMember } from '../lib/data';
import { listReferralsReceived, listReferralsSent, getReferralById } from '../lib/referrals';
import ReferralDetailsModal from '../components/ReferralDetailsModal';

function GuestRequestModal({ open, onClose }) {
  const [businessName, setBusinessName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [servicesProvided, setServicesProvided] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = useMemo(() => {
    // Contract (unauth guest request): require Name + Email + Phone + Industry.
    return (
      name.trim().length > 0
      && email.trim().length > 0
      && phone.trim().length > 0
      && industry.trim().length > 0
    );
  }, [name, email, phone, industry]);

  useEffect(() => {
    if (!open) return;

    setSubmitted(false);
    setError('');
    setSubmitting(false);

    setBusinessName('');
    setName('');
    setEmail('');
    setPhone('');
    setIndustry('');
    setServicesProvided('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-lg">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-NBNA-primary">Request to Attend Next Meeting</h2>
          <button type="button" className="text-sm text-gray-600" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="px-6 py-5">
          {submitted ? (
            <div className="space-y-2">
              <p className="text-gray-900">Request received.</p>
              <p className="text-gray-700">Email sent — check your email for meeting details.</p>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();

                if (!canSubmit || submitting) return;

                setSubmitting(true);
                setError('');

                try {
                  await apiClient.post(
                    '/api/guest-requests',
                    {
                      businessName: businessName.trim() || 'N/A',
                      name,
                      email,
                      phone: phone.trim() || 'N/A',
                      industry: industry.trim() || 'N/A',
                      servicesProvided: servicesProvided.trim() || 'N/A',
                      asGuestOfMemberId: null,
                    },
                    { skipAuth: true }
                  );

                  setSubmitted(true);
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Request failed');
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="guest-business-name">
                  Business Name
                </label>
                <input
                  id="guest-business-name"
                  name="businessName"
                  type="text"
                  className="input-field mt-1 w-full"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  aria-label="Business Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="guest-name">
                  Name
                </label>
                <input
                  id="guest-name"
                  name="name"
                  type="text"
                  className="input-field mt-1 w-full"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="guest-email">
                  Email
                </label>
                <input
                  id="guest-email"
                  name="email"
                  type="email"
                  className="input-field mt-1 w-full"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="guest-phone">
                  Phone
                </label>
                <input
                  id="guest-phone"
                  name="phone"
                  type="tel"
                  className="input-field mt-1 w-full"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="guest-industry">
                  Industry
                </label>
                <input
                  id="guest-industry"
                  name="industry"
                  type="text"
                  className="input-field mt-1 w-full"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="guest-services-provided">
                  Services Provided
                </label>
                <textarea
                  id="guest-services-provided"
                  name="servicesProvided"
                  className="input-field mt-1 w-full"
                  rows={3}
                  value={servicesProvided}
                  onChange={(e) => setServicesProvided(e.target.value)}
                />
              </div>

              {error ? <div className="text-sm text-red-600">{error}</div> : null}

              <button type="submit" className="btn-primary" disabled={!canSubmit || submitting}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function IndustriesSought() {
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    let mounted = true;
    getPublicIndustriesSought()
      .then((items) => {
        if (!mounted) return;
        if (Array.isArray(items) && items.length > 0) setIndustries(items);
      })
      .catch(() => {
        // fallback to local content
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="mt-4">
      <h3 className="text-sm font-semibold text-gray-900">Industries sought</h3>
      <ul className="mt-2 list-disc pl-5 text-gray-700">
        {industries.map((industry) => (
          <li key={industry}>{industry}</li>
        ))}
      </ul>
    </div>
  );
}

function formatCurrency(value) {
  // NBNA: show values without hard-coding a currency (USD/CAD/etc).
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

function pickMetricComparison({ value, average }) {
  const numericValue = Number(value);
  const numericAverage = Number(average);

  if (!Number.isFinite(numericValue) || !Number.isFinite(numericAverage) || numericAverage <= 0) {
    return {
      label: 'Below average',
      detail: 'Not enough data to compute an average yet.',
    };
  }

  if (numericValue >= numericAverage) {
    return { label: 'Above average', detail: null };
  }

  return { label: 'Below average', detail: null };
}

function TodayNetworkingCard() {
  const [match, setMatch] = useState(null);

  useEffect(() => {
    try {
      import('../lib/meetingMatch').then(({ readTodayMatch }) => {
        setMatch(readTodayMatch());
      });
    } catch {
      setMatch(null);
    }
  }, []);

  return (
    <>
      <h3 className="text-lg font-semibold text-NBNA-primary">Today’s Networking</h3>
      {match?.member?.id ? (
        <div className="mt-2 space-y-1">
          <p className="text-gray-700">Matched with: {match.member.displayName || 'Member'}</p>
          <a className="text-NBNA-primary underline" href={`/members/${match.member.id}`}>
            View profile
          </a>
          {match.member.googleBusinessProfileUrl ? (
            <a
              className="block text-NBNA-primary underline"
              href={match.member.googleBusinessProfileUrl}
              target="_blank"
              rel="noreferrer"
            >
              Write a Google review
            </a>
          ) : null}
        </div>
      ) : (
        <div className="mt-2 space-y-1">
          <p className="text-gray-700">No match yet.</p>
        </div>
      )}
    </>
  );
}

export default function Home({ isAuthenticated, user }) {
  const navigate = useNavigate();
  const [meetingIsOpen, setMeetingIsOpen] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);

  const [timeframe, setTimeframe] = useState('year');
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [dashboardMetricsLoading, setDashboardMetricsLoading] = useState(false);
  const [dashboardMetricsError, setDashboardMetricsError] = useState(null);

  const [myMember, setMyMember] = useState(null);

  const [referralsReceived, setReferralsReceived] = useState([]);
  const [referralsSent, setReferralsSent] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsError, setReferralsError] = useState(null);

  const [selectedReferral, setSelectedReferral] = useState(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);

  const dashboardScope = useMemo(() => {
    // Spec note: members typically view their own stats; admins/executives may view org-level stats.
    // We do not add a separate scope toggle yet (not required by current E2E UI contract).
    const role = user?.uiRole || user?.role || user?.backendRole;
    if (!role) return 'member';
    const normalized = String(role).toLowerCase();
    if (normalized.includes('admin') || normalized.includes('exec')) return 'org';
    return 'member';
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const controller = new AbortController();
    setDashboardMetricsLoading(true);
    setDashboardMetricsError(null);

    apiClient
      .get('/dashboard/metrics', {
        params: {
          timeframe,
          scope: dashboardScope,
        },
        signal: controller.signal,
      })
      .then((data) => {
        setDashboardMetrics(data);
      })
      .catch((error) => {
        // Endpoint may not exist yet; render UI contract without fabricating numeric values.
        setDashboardMetrics(null);
        setDashboardMetricsError(error);
      })
      .finally(() => {
        setDashboardMetricsLoading(false);
      });

    return () => controller.abort();
  }, [dashboardScope, isAuthenticated, timeframe]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        const member = await getMyMember();
        if (!cancelled) setMyMember(member);
      } catch {
        if (!cancelled) setMyMember(null);
      }

      setReferralsLoading(true);
      setReferralsError(null);
      try {
        const [received, sent] = await Promise.all([
          listReferralsReceived(),
          listReferralsSent(),
        ]);
        if (!cancelled) {
          setReferralsReceived(Array.isArray(received) ? received : []);
          setReferralsSent(Array.isArray(sent) ? sent : []);
        }
      } catch (err) {
        if (!cancelled) {
          setReferralsReceived([]);
          setReferralsSent([]);
          setReferralsError(err);
        }
      } finally {
        if (!cancelled) setReferralsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    (async () => {
      try {
        await getTodayMeeting();
        if (!cancelled) setMeetingIsOpen(true);
      } catch {
        if (!cancelled) setMeetingIsOpen(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, navigate]);

  const businessGenerated = dashboardMetrics?.businessGenerated
    ?? dashboardMetrics?.business_generated
    ?? 0;
  const salesReceived = dashboardMetrics?.salesReceived
    ?? dashboardMetrics?.sales_received
    ?? 0;
  const referralsPassed = dashboardMetrics?.referralsPassed
    ?? dashboardMetrics?.referrals_passed
    ?? 0;

  const averageBusinessGenerated = dashboardMetrics?.averages?.businessGenerated
    ?? dashboardMetrics?.averages?.business_generated
    ?? dashboardMetrics?.averageBusinessGenerated
    ?? null;

  const comparison = pickMetricComparison({
    value: businessGenerated,
    average: averageBusinessGenerated,
  });
  const shouldShowRegisterMode = meetingIsOpen;

  const openReferral = async (ref) => {
    if (!ref) return;

    setSelectedReferral(ref);
    setReferralModalOpen(true);

    try {
      const hydrated = await getReferralById(ref.id);
      setSelectedReferral(hydrated);
    } catch {
      // keep best-effort details
    }
  };

  const handleReferralUpdated = (updated) => {
    if (!updated) return;

    setSelectedReferral(updated);

    setReferralsReceived((prev) => (prev || []).map((r) => (r.id === updated.id ? updated : r)));
    setReferralsSent((prev) => (prev || []).map((r) => (r.id === updated.id ? updated : r)));

    // If the referral was archived, drop it from the relevant list(s).
    setReferralsReceived((prev) => (prev || []).filter((r) => !r.archivedByReceiver));
    setReferralsSent((prev) => (prev || []).filter((r) => !r.archivedByReferrer));
  };

  if (!isAuthenticated) {
    return (
      <div className="space-y-10">
        <section className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-NBNA-primary">{HOME_LANDING_CONTENT.about.heading}</h2>
          <p className="mt-2 text-gray-700">{HOME_LANDING_CONTENT.about.body}</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button type="button" className="btn-primary" onClick={() => setGuestModalOpen(true)}>
              {HOME_LANDING_CONTENT.about.cta.label}
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/prospect')}>
              Apply / Join
            </button>
          </div>
        </section>

        <section className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-NBNA-primary">{HOME_LANDING_CONTENT.faq.heading}</h2>
          <ul className="mt-4 space-y-3">
            {HOME_LANDING_CONTENT.faq.items.map((item) => (
              <li key={item.question} className="text-gray-700">
                <div className="font-semibold">{item.question}</div>
                <div className="mt-1 text-sm">{item.answer}</div>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-NBNA-primary">{HOME_LANDING_CONTENT.howToJoin.heading}</h2>
          <p className="mt-2 text-gray-700">{HOME_LANDING_CONTENT.howToJoin.body}</p>
          <IndustriesSought />
        </section>

        <GuestRequestModal open={guestModalOpen} onClose={() => setGuestModalOpen(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="bg-white shadow-md rounded-lg p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-2xl font-semibold text-NBNA-primary">Dashboard</h2>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="dashboard-timeframe">
              Timeframe
            </label>
            <select
              id="dashboard-timeframe"
              className="input-field w-28"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              aria-label="Timeframe"
            >
              <option value="year">year</option>
              <option value="month">month</option>
              <option value="week">week</option>
            </select>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-md border p-4">
            <div className="text-sm text-gray-500">Business Generated</div>
            <div className="text-xl font-semibold text-gray-900">{formatCurrency(businessGenerated)}</div>
          </div>
          <div className="rounded-md border p-4">
            <div className="text-sm text-gray-500">Sales Received</div>
            <div className="text-xl font-semibold text-gray-900">{formatCurrency(salesReceived)}</div>
          </div>
          <div className="rounded-md border p-4">
            <div className="text-sm text-gray-500">Referrals Passed</div>
            <div className="text-xl font-semibold text-gray-900">{formatCurrency(referralsPassed)}</div>
          </div>
        </div>

        <div className="mt-4 rounded-md border bg-gray-50 p-4">
          <div className="text-sm font-medium text-gray-900">
            {comparison.label}
          </div>
          {comparison.detail ? <div className="mt-1 text-sm text-gray-700">{comparison.detail}</div> : null}
          {dashboardMetricsLoading ? (
            <div className="mt-1 text-xs text-gray-500">Loading metrics…</div>
          ) : null}
          {dashboardMetricsError ? (
            <div className="mt-1 text-xs text-gray-500">Comparison unavailable.</div>
          ) : null}
        </div>

        {shouldShowRegisterMode ? (
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/meeting/host')}
            >
              Enter Register Mode
            </button>
            <div className="text-sm text-gray-600 sm:self-center">
              Meeting registration is open.
            </div>
          </div>
        ) : null}
      </section>
      {meetingIsOpen ? (
        <section className="bg-white shadow-md rounded-lg p-6">
          <TodayNetworkingCard />
        </section>
      ) : null}

      <section className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-semibold text-NBNA-primary">Referrals received</h2>
        {referralsLoading ? (
          <div className="mt-2 text-sm text-gray-500">Loading…</div>
        ) : null}
        {referralsError ? (
          <div className="mt-2 text-sm text-gray-500">Unable to load referrals.</div>
        ) : null}

        <ul className="mt-4 space-y-2">
          {(referralsReceived ?? []).length ? (
            referralsReceived.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="w-full rounded-md border px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => openReferral(r)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900">{r.contactName}</div>
                      <div className="text-sm text-gray-600">
                        From: {r.counterparty?.businessName || r.counterparty?.name || 'Member'}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">{formatCurrency(r.workflowSaleAmount)}</div>
                  </div>
                </button>
              </li>
            ))
          ) : (
            <li className="text-sm text-gray-500">No referrals received.</li>
          )}
        </ul>
      </section>

      <section className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-semibold text-NBNA-primary">Referrals sent</h2>
        <ul className="mt-4 space-y-2">
          {(referralsSent ?? []).length ? (
            referralsSent.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="w-full rounded-md border px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => openReferral(r)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900">{r.contactName}</div>
                      <div className="text-sm text-gray-600">
                        To: {r.counterparty?.businessName || r.counterparty?.name || 'Member'}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">{formatCurrency(r.workflowSaleAmount)}</div>
                  </div>
                </button>
              </li>
            ))
          ) : (
            <li className="text-sm text-gray-500">No referrals sent.</li>
          )}
        </ul>
      </section>

      <ReferralDetailsModal
        isOpen={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
        referral={selectedReferral}
        myMemberId={myMember?.id ?? null}
        onUpdated={handleReferralUpdated}
      />

    </div>
  );
}
